import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { UserCheck, Search, Check, XCircle, Mail, Phone, IdCard } from "lucide-react";

export default function PendingRequestsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/employees/pending-requests")).data); }
    catch (_) { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (rid, name) => {
    if (!window.confirm(`Approvare l'iscrizione di ${name}?`)) return;
    try {
      await api.post(`/employees/pending-requests/${rid}/approve`);
      toast.success("Dipendente approvato · email inviata");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const reject = async (rid, name) => {
    const note = window.prompt(`Motivo del rifiuto per ${name} (visibile internamente):`, "");
    if (note === null) return;
    try {
      await api.post(`/employees/pending-requests/${rid}/reject`, { note });
      toast.success("Richiesta rifiutata · dati cancellati");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const filtered = items.filter((p) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (`${p.name} ${p.lastname}`.toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q) || (p.fiscal_code || "").toLowerCase().includes(q));
  });

  return (
    <div className="p-4 md:p-12" data-testid="pending-requests-page">
      <div className="mb-8 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Onboarding · Approvazioni</div>
          <h1 className="font-display text-4xl">Richieste iscrizione</h1>
          <p className="text-[#555] mt-1 text-sm">Dipendenti che si sono auto-registrati indicando la P.IVA della tua azienda.</p>
        </div>
        <div className="inline-flex items-center gap-2 bg-[#1E2347] border border-[#3B82F6]/30 rounded-full px-4 py-2">
          <UserCheck className="w-4 h-4 text-[#3B82F6]"/>
          <span className="text-sm font-bold text-[#7C3AED]" data-testid="pending-count">{items.length} in attesa</span>
        </div>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#999]"/>
        <Input
          placeholder="Cerca per nome, email o codice fiscale…"
          className="rounded-xl pl-9"
          value={query}
          onChange={(e)=>setQuery(e.target.value)}
          data-testid="pending-search"
        />
      </div>

      {loading ? (
        <div className="text-center text-[#757575] py-12">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="card-smart p-12 text-center text-[#757575]" data-testid="pending-empty">
          <UserCheck className="w-12 h-12 mx-auto mb-3 text-[#3B82F6]/50"/>
          <div className="font-display text-xl mb-1">Nessuna richiesta in attesa</div>
          <div className="text-xs">Condividi il link <span className="font-mono bg-white px-2 py-0.5 rounded border border-[#1F2540]">/register-employee</span> con i tuoi dipendenti.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((p) => (
            <div key={p.id} className="card-smart p-5" data-testid={`request-${p.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-2xl bg-[#1E2347] text-[#3B82F6] flex items-center justify-center text-lg font-bold">
                      {(p.name?.[0] || "?").toUpperCase()}{(p.lastname?.[0] || "").toUpperCase()}
                    </div>
                    <div>
                      <div className="font-display text-xl">{p.name} {p.lastname}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#757575]">
                        Inviata {new Date(p.created_at).toLocaleString("it-IT")}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                    <div className="flex items-center gap-1.5 text-[#555]"><Mail className="w-3.5 h-3.5 text-[#3B82F6]"/>{p.email}</div>
                    <div className="flex items-center gap-1.5 text-[#555]"><Phone className="w-3.5 h-3.5 text-[#3B82F6]"/>{p.phone || "—"}</div>
                    <div className="flex items-center gap-1.5 text-[#555] font-mono"><IdCard className="w-3.5 h-3.5 text-[#3B82F6]"/>{p.fiscal_code || "—"}</div>
                  </div>
                  <div className="mt-2 text-[10px] uppercase tracking-widest text-[#999]">
                    Azienda dichiarata: <span className="text-[#0A0A0A]">{p.company_name_declared}</span> · P.IVA {p.vat_number}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={()=>approve(p.id, `${p.name} ${p.lastname}`)}
                    data-testid={`approve-${p.id}`}
                    className="rounded-full bg-[#3B82F6] hover:bg-black text-white h-11"
                  >
                    <Check className="w-4 h-4 mr-2"/> Approva
                  </Button>
                  <Button
                    onClick={()=>reject(p.id, `${p.name} ${p.lastname}`)}
                    variant="outline"
                    data-testid={`reject-${p.id}`}
                    className="rounded-full border-red-300 text-red-600 hover:bg-red-50 h-11"
                  >
                    <XCircle className="w-4 h-4 mr-2"/> Rifiuta
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
